/**
 * toReservationRowView — pure DTO → view mapper for the ReservationsGrid
 * (SCRUM-334).
 *
 * The grid never consumes the raw `ReservationListItemDto`: it consumes a
 * `ReservationRowView` computed here. Keeping the projection PURE (no
 * hooks, no I/O, no wall-clock read) makes it unit-testable in isolation
 * (see `__tests__/to-reservation-row-view.spec.ts`) and keeps the grid
 * component a thin renderer.
 *
 * Computed fields:
 *   - `nights`      — whole calendar days between `arrivalDate` and
 *     `departureDate` (both `YYYY-MM-DD`). Computed via UTC-midnight epoch
 *     arithmetic so it is DST-/timezone-immune (never local-`Date` math).
 *   - `modified`    — `currentSnapshotVersion != null && > 1`. Faithful
 *     "charges re-frozen after create" signal (create is 1; pure status
 *     transitions never bump it); feeds `ResStatusPill`'s `modified` prop.
 *   - `glyphKind`   — via the shipped `glyphKindFor(stayKind,
 *     reservationTypeKey, bookingChannel)`; `processor` feeds the
 *     `bookingChannel` param so a paying guest with a non-null processor
 *     upgrades `directGuest → channelGuest`.
 *   - `sourceLabel` — a known-processor display map (`airbnb`→"Airbnb",
 *     `vrbo`→"VRBO", titlecase fallback) when `processor` is non-null;
 *     otherwise a coarse `stayKind`-derived label (PayingGuest→"Direct",
 *     OwnerStay→"Owner", NonStayBlock→"Internal").
 *   - `propertyName`— `nameById.get(propertyId)`, falling back to a short
 *     `propertyId` prefix when the name is not (yet) resolved.
 *
 * Money (`grandTotalCents`, `balanceDueCents`) is passed through UNCHANGED
 * — never `/100`, never null→0 coercion. The grid formats via
 * `formatCents` at the display boundary.
 */

import type { ReservationListItemDto } from './types.js';
import { glyphKindFor, type TypeGlyphKind } from '../_components/TypeIconBadge.js';

/**
 * The row view the grid renders. Computed fields sit alongside the
 * passthrough wire scalars the cells need (id for row→detail navigation,
 * status for the pill, guest name, raw dates for the stay cell, raw Cents
 * for the money cells).
 */
export interface ReservationRowView {
  /** Stable reservation id — row key + `→ /reservations/:id` target. */
  readonly id: string;
  /** Property id (unresolved fallback source for `propertyName`). */
  readonly propertyId: string;
  /** Resolved property display name, or a short `propertyId` fallback. */
  readonly propertyName: string;
  /**
   * Guest display name (PII-safe short name from the DTO), or `null` for a
   * guest-less reservation (NonStayBlock, or a row with no name on file).
   * Mirrors the wire (`ReservationListItemDto.guestDisplayName: string | null`)
   * and the detail view — the renderer supplies the "no name" fallback.
   */
  readonly guestDisplayName: string | null;
  /** Wire FSM status (`QUO | HOLD | BKD | CI | CO | CXL | HIST`). */
  readonly status: string;
  /** Opaque reservation-type key (`null` = typeless manual create). */
  readonly reservationTypeKey: string | null;
  /** Half-open stay window (calendar dates, `YYYY-MM-DD`). */
  readonly arrivalDate: string;
  readonly departureDate: string;
  /** Whole nights (departure − arrival, calendar days). */
  readonly nights: number;
  /** `true` when charges were re-frozen after create (snapshotVersion>1). */
  readonly modified: boolean;
  /** TypeIconBadge glyph bucket (stayKind + processor channel upgrade). */
  readonly glyphKind: TypeGlyphKind;
  /** Human "Source" label (known processor, or stayKind-derived). */
  readonly sourceLabel: string;
  /** DISPLAY-ONLY Cents — passthrough; `null` for legacy pre-273 rows. */
  readonly grandTotalCents: number | null;
  /** DISPLAY-ONLY Cents — passthrough (negative = refund owed). */
  readonly balanceDueCents: number | null;
}

/** Known channel processors → their display label. */
const PROCESSOR_LABELS: Readonly<Record<string, string>> = {
  airbnb: 'Airbnb',
  vrbo: 'VRBO',
  bookingcom: 'Booking.com',
  expedia: 'Expedia',
  website: 'Website',
  direct: 'Direct',
};

/** Coarse `stayKind`-derived source label when no processor is recorded. */
const STAY_KIND_SOURCE_LABELS: Readonly<Record<string, string>> = {
  PayingGuest: 'Direct',
  OwnerStay: 'Owner',
  NonStayBlock: 'Internal',
};

/**
 * Whole calendar days between two `YYYY-MM-DD` strings, computed at UTC
 * midnight so the result is timezone-/DST-immune. Parses the components
 * explicitly (never `new Date(string)`, which is locale-ambiguous) and
 * diffs UTC epoch millis. Returns 0 for a malformed / non-parsable input
 * rather than `NaN` (defensive; the wire type guarantees `YYYY-MM-DD`).
 *
 * Exported (SCRUM-335) so `to-reservation-detail-view.ts` reuses the exact
 * same UTC-midnight night arithmetic — no duplicated date math across the
 * row-view and detail-view mappers.
 */
export function nightsBetween(arrivalDate: string, departureDate: string): number {
  const arrival = utcMidnightMillis(arrivalDate);
  const departure = utcMidnightMillis(departureDate);
  if (arrival === null || departure === null) return 0;
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.round((departure - arrival) / MS_PER_DAY);
}

function utcMidnightMillis(date: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return Date.UTC(year, month - 1, day);
}

/** TitleCase an unknown processor token for a legible fallback label. */
function titleCase(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/**
 * Human "Source" label from a channel `processor` (known-processor map,
 * titlecase fallback) or, absent a processor, a coarse `stayKind`-derived
 * label. Exported (SCRUM-335) so the detail-view mapper reuses the exact
 * same processor→label map — never re-implemented.
 */
export function sourceLabelFor(
  processor: string | null | undefined,
  stayKind: string,
): string {
  if (typeof processor === 'string' && processor.trim().length > 0) {
    const key = processor.trim().toLowerCase().replace(/[.\-_\s]/g, '');
    return PROCESSOR_LABELS[key] ?? titleCase(processor);
  }
  return STAY_KIND_SOURCE_LABELS[stayKind] ?? 'Direct';
}

/**
 * Short `propertyId` fallback for when a name is not resolved — the first
 * segment of the UUID, never an empty string (a silent-failure smell).
 */
function shortPropertyId(propertyId: string): string {
  const head = propertyId.split('-')[0] ?? propertyId;
  return head.length > 0 ? head : propertyId;
}

export function toReservationRowView(
  dto: ReservationListItemDto,
  nameById: ReadonlyMap<string, string>,
): ReservationRowView {
  const modified =
    dto.currentSnapshotVersion !== null &&
    dto.currentSnapshotVersion !== undefined &&
    dto.currentSnapshotVersion > 1;

  return {
    id: dto.id,
    propertyId: dto.propertyId,
    propertyName: nameById.get(dto.propertyId) ?? shortPropertyId(dto.propertyId),
    guestDisplayName: dto.guestDisplayName,
    status: dto.status,
    reservationTypeKey: dto.reservationTypeKey,
    arrivalDate: dto.arrivalDate,
    departureDate: dto.departureDate,
    nights: nightsBetween(dto.arrivalDate, dto.departureDate),
    modified,
    // `processor` feeds the `bookingChannel` param → channel upgrade.
    glyphKind: glyphKindFor(dto.stayKind, dto.reservationTypeKey, dto.processor),
    sourceLabel: sourceLabelFor(dto.processor, dto.stayKind),
    // Money passthrough — never coerce null/zero, never /100.
    grandTotalCents: dto.grandTotalCents,
    balanceDueCents: dto.balanceDueCents,
  };
}
